import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { ConflictResolutionModal } from './ConflictResolutionModal';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export const RealtimeSync = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isConnected, setIsConnected] = useState(false);
  const [showIndicator, setShowIndicator] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);
  const { user } = useAuth();
  const { pendingCount, isSyncing, conflicts, resolveConflict, resolveAllConflicts } = useOfflineSync();

  useEffect(() => {
    // Monitor online status
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!user || !isOnline) {
      setIsConnected(false);
      return;
    }

    // Create realtime connection
    const channel = supabase
      .channel('sync-indicator')
      .on('presence', { event: 'sync' }, () => {
        setIsConnected(true);
      })
      .on('presence', { event: 'join' }, () => {
        setIsConnected(true);
      })
      .on('presence', { event: 'leave' }, () => {
        setIsConnected(false);
      });

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        setIsConnected(true);
        channel.track({ user_id: user.id, online_at: new Date().toISOString() });
      } else {
        setIsConnected(false);
      }
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isOnline]);

  // Reload page cuando vuelve internet y termina de sincronizar
  useEffect(() => {
    if (wasOffline && isOnline && !isSyncing && pendingCount === 0) {
      // Esperar 1 segundo después de sincronizar para hacer reload
      const timeout = setTimeout(() => {
        window.location.reload();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [wasOffline, isOnline, isSyncing, pendingCount]);

  // Keep expanded while there's something requiring attention; otherwise auto-collapse after 3s
  const mustStayExpanded = !isOnline || isSyncing || pendingCount > 0;

  useEffect(() => {
    setShowIndicator(true);
    if (mustStayExpanded) return;
    const t = setTimeout(() => setShowIndicator(false), 3000);
    return () => clearTimeout(t);
  }, [isOnline, isSyncing, pendingCount, mustStayExpanded]);

  const expandTemporarily = () => {
    setShowIndicator(true);
    if (mustStayExpanded) return;
    setTimeout(() => setShowIndicator(false), 3000);
  };

  // Determine status and styling — iOS-style glassmorphism
  const glassBase =
    "backdrop-blur-xl backdrop-saturate-150 border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.18)] ring-1 ring-white/20";

  const getStatus = () => {
    if (isSyncing) {
      return {
        icon: <RefreshCw className="h-3.5 w-3.5 animate-spin" />,
        text: "Sincronizando...",
        className: `${glassBase} bg-slate-900/40 text-white`,
        dot: "bg-green-400",
      };
    }

    if (pendingCount > 0) {
      return {
        icon: <AlertCircle className="h-3.5 w-3.5" />,
        text: `${pendingCount} pendiente${pendingCount > 1 ? 's' : ''}`,
        className: `${glassBase} bg-slate-900/40 text-white`,
        dot: "bg-amber-400",
      };
    }

    if (!isOnline) {
      return {
        icon: <WifiOff className="h-3.5 w-3.5" />,
        text: "Offline",
        className: `${glassBase} bg-slate-900/40 text-white`,
        dot: "bg-red-400",
      };
    }

    return {
      icon: <Wifi className="h-3.5 w-3.5" />,
      text: "Online",
      className: `${glassBase} bg-slate-900/40 text-white`,
      dot: "bg-emerald-400",
    };
  };

  const status = getStatus();

  if (!user) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 animate-fade-in">
        {showIndicator ? (
          <div
            className={cn(
              "relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium tracking-tight transition-all duration-300 overflow-hidden",
              status.className
            )}
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/40 to-transparent" />
            <span className={cn("relative h-2 w-2 rounded-full shadow-[0_0_8px_currentColor]", status.dot)} />
            <span className="relative flex items-center gap-1.5">
              {status.icon}
              {status.text}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onMouseEnter={expandTemporarily}
            onClick={expandTemporarily}
            aria-label={`Estado: ${status.text}`}
            className={cn(
              "relative h-3 w-3 rounded-full transition-all duration-300 hover:scale-125",
              status.dot,
              "shadow-[0_0_10px_currentColor] ring-1 ring-white/40"
            )}
          />
        )}
      </div>

      {conflicts.length > 0 && (
        <ConflictResolutionModal
          conflicts={conflicts}
          onResolve={resolveConflict}
          onResolveAll={resolveAllConflicts}
        />
      )}
    </>
  );
};
